import { useCallback, useEffect, useRef } from 'react';
import { StepData } from '@packages/global';

/**
 * 本地备份数据结构
 */
interface BackupData {
  scriptId: string;
  stepNumber: number;
  data: Partial<StepData>;
  timestamp: number;
  version: number;
}

/**
 * 备份存储键
 */
const BACKUP_STORAGE_KEY = 'script-generation-backups';
const BACKUP_VERSION = 1;
const MAX_BACKUPS_PER_SCRIPT = 6; // 每个脚本最多保存 6 个步骤的备份
const BACKUP_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 天过期

/**
 * 本地备份 Hook
 * 
 * 提供自动备份和恢复功能，用于防止数据丢失
 * 
 * Requirements: 12.4, 12.5
 */
export function useLocalBackup(scriptId: string | undefined) {
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * 获取所有备份
   */
  const getAllBackups = useCallback((): BackupData[] => {
    try {
      const stored = localStorage.getItem(BACKUP_STORAGE_KEY);
      if (!stored) return [];

      const backups: BackupData[] = JSON.parse(stored);

      // 过滤过期备份
      const now = Date.now();
      return backups.filter(b => now - b.timestamp < BACKUP_EXPIRY_MS);
    } catch (err) {
      console.error('读取本地备份失败:', err);
      return [];
    }
  }, []);

  /**
   * 保存所有备份
   */
  const saveAllBackups = useCallback((backups: BackupData[]) => {
    try {
      localStorage.setItem(BACKUP_STORAGE_KEY, JSON.stringify(backups));
    } catch (err) {
      console.error('保存本地备份失败:', err);
    }
  }, []);

  /**
   * 备份步骤数据
   */
  const backupStep = useCallback((stepNumber: number, data: Partial<StepData>) => {
    if (!scriptId) return;

    const backups = getAllBackups();

    // 查找现有备份
    const existingIndex = backups.findIndex(
      b => b.scriptId === scriptId && b.stepNumber === stepNumber
    );

    const newBackup: BackupData = {
      scriptId,
      stepNumber,
      data,
      timestamp: Date.now(),
      version: BACKUP_VERSION,
    };

    if (existingIndex >= 0) {
      // 更新现有备份
      backups[existingIndex] = newBackup;
    } else {
      // 添加新备份
      backups.push(newBackup);
    }

    // 限制每个脚本的备份数量
    const scriptBackups = backups.filter(b => b.scriptId === scriptId);
    if (scriptBackups.length > MAX_BACKUPS_PER_SCRIPT) {
      // 删除最旧的备份
      const oldestBackup = scriptBackups.reduce((oldest, current) =>
        current.timestamp < oldest.timestamp ? current : oldest
      );
      const oldestIndex = backups.findIndex(
        b => b.scriptId === oldestBackup.scriptId &&
          b.stepNumber === oldestBackup.stepNumber &&
          b.timestamp === oldestBackup.timestamp
      );
      if (oldestIndex >= 0) {
        backups.splice(oldestIndex, 1);
      }
    }

    saveAllBackups(backups);
  }, [scriptId, getAllBackups, saveAllBackups]);

  /**
   * 带防抖的备份
   */
  const backupStepDebounced = useCallback((
    stepNumber: number,
    data: Partial<StepData>,
    delay: number = 2000
  ) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      backupStep(stepNumber, data);
    }, delay);
  }, [backupStep]);

  /**
   * 恢复步骤数据
   */
  const restoreStep = useCallback((stepNumber: number): Partial<StepData> | null => {
    if (!scriptId) return null;

    const backups = getAllBackups();
    const backup = backups.find(
      b => b.scriptId === scriptId && b.stepNumber === stepNumber
    );

    return backup?.data || null;
  }, [scriptId, getAllBackups]);

  /**
   * 获取脚本的所有备份
   */
  const getScriptBackups = useCallback((): BackupData[] => {
    if (!scriptId) return [];

    const backups = getAllBackups();
    return backups
      .filter(b => b.scriptId === scriptId)
      .sort((a, b) => a.stepNumber - b.stepNumber);
  }, [scriptId, getAllBackups]);

  /**
   * 检查是否有未恢复的备份
   */
  const hasUnrestoredBackups = useCallback((): boolean => {
    return getScriptBackups().length > 0;
  }, [getScriptBackups]);

  /**
   * 清除步骤备份
   */
  const clearStepBackup = useCallback((stepNumber: number) => {
    if (!scriptId) return;

    const backups = getAllBackups();
    const filteredBackups = backups.filter(
      b => !(b.scriptId === scriptId && b.stepNumber === stepNumber)
    );
    saveAllBackups(filteredBackups);
  }, [scriptId, getAllBackups, saveAllBackups]);

  /**
   * 清除脚本的所有备份
   */
  const clearScriptBackups = useCallback(() => {
    if (!scriptId) return;

    const backups = getAllBackups();
    const filteredBackups = backups.filter(b => b.scriptId !== scriptId);
    saveAllBackups(filteredBackups);
  }, [scriptId, getAllBackups, saveAllBackups]);

  /**
   * 清除所有过期备份
   */
  const cleanupExpiredBackups = useCallback(() => {
    const backups = getAllBackups();
    const now = Date.now();
    const validBackups = backups.filter(b => now - b.timestamp < BACKUP_EXPIRY_MS);

    if (validBackups.length !== backups.length) {
      saveAllBackups(validBackups);
    }
  }, [getAllBackups, saveAllBackups]);

  /**
   * 获取备份时间戳
   */
  const getBackupTimestamp = useCallback((stepNumber: number): Date | null => {
    if (!scriptId) return null;

    const backups = getAllBackups();
    const backup = backups.find(
      b => b.scriptId === scriptId && b.stepNumber === stepNumber
    );

    return backup ? new Date(backup.timestamp) : null;
  }, [scriptId, getAllBackups]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 定期清理过期备份
  useEffect(() => {
    cleanupExpiredBackups();

    // 每小时清理一次
    const interval = setInterval(cleanupExpiredBackups, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanupExpiredBackups]);

  return {
    // 备份方法
    backupStep,
    backupStepDebounced,

    // 恢复方法
    restoreStep,
    getScriptBackups,
    hasUnrestoredBackups,

    // 清理方法
    clearStepBackup,
    clearScriptBackups,
    cleanupExpiredBackups,

    // 工具方法
    getBackupTimestamp,
  };
}

export type UseLocalBackupReturn = ReturnType<typeof useLocalBackup>;
